import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

/** One row from GET /api/me/notifications (RLS-scoped to the current user on the server). */
export interface Notification {
  id: string;
  type: string;
  entityType: string | null;
  entityId: string | null;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsValue {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  /** Re-fetch the list + unread count (e.g. when the panel opens). */
  refresh: () => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsValue | null>(null);

/** Poll the unread count on this cadence — cheap, and matches the messaging poll posture (§4.3). */
const POLL_MS = 30_000;

async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { headers: { accept: "application/json" }, ...init });
  if (!res.ok) throw new Error(String(res.status));
  return res.json() as Promise<T>;
}

/**
 * Loads the current user's in-app notifications and keeps the unread count fresh (architecture
 * §6.2 — the bell badge). Mirrors AuthzProvider's plain-fetch approach (no TanStack yet); mount
 * inside the authenticated shell tree so it only runs once a session exists.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(() => {
    Promise.all([
      getJson<Notification[]>("/me/notifications"),
      getJson<{ count: number }>("/me/notifications/unread-count"),
    ])
      .then(([list, { count }]) => {
        if (!mounted.current) return;
        setItems(list);
        setUnreadCount(count);
      })
      .catch(() => {
        /* transient — keep the last good state, the poll will retry */
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
  }, []);

  const refreshCount = useCallback(() => {
    getJson<{ count: number }>("/me/notifications/unread-count")
      .then(({ count }) => mounted.current && setUnreadCount(count))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const id = window.setInterval(refreshCount, POLL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
    };
  }, [refresh, refreshCount]);

  const markRead = useCallback(async (id: string) => {
    await getJson(`/me/notifications/${id}/read`, { method: "POST" }).catch(() => undefined);
    if (!mounted.current) return;
    setItems((prev) => prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await getJson("/me/notifications/read-all", { method: "POST" }).catch(() => undefined);
    if (!mounted.current) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
  }, []);

  const value = useMemo<NotificationsValue>(
    () => ({ items, unreadCount, loading, refresh, markRead, markAllRead }),
    [items, unreadCount, loading, refresh, markRead, markAllRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

/** Access the notifications context. Throws if used outside <NotificationsProvider>. */
export function useNotifications(): NotificationsValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within <NotificationsProvider>");
  return ctx;
}
